/*
 * © 2026 eggs unimedia GmbH
 * Coded with love 💛
 */

import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Text, Alert, MicrosoftButton, LinkedInButton } from './index';
import { msalAuthService } from '../services/msalAuthService';

/**
 * WelcomeScreen - Splash screen for unauthenticated users
 * Clean, focused entry point with social login options
 */
export default function WelcomeScreen() {
  const navigate = useNavigate();
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');

  const initiateOAuthLogin = async (provider) => {
    const { signInWithRedirect, getCurrentUser, signOut } = await import('aws-amplify/auth');
    
    try {
      await getCurrentUser();
      await signOut();
    } catch {
      // No existing session — proceed
    }

    const originalSetItem = sessionStorage.setItem.bind(sessionStorage);
    sessionStorage.setItem = function(key, value) {
      originalSetItem(key, value);
      if (key.includes('code_verifier') || key.includes('pkce')) {
        localStorage.setItem(key, value);
      }
    };
    
    await signInWithRedirect({ provider: { custom: provider } });
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-gray-50 to-gray-100 px-4">
      <div className="w-full max-w-md">
        {/* Hero Section */}
        <div className="text-center mb-10">
          <div className="w-24 h-24 bg-eggs-yellow rounded-full flex items-center justify-center mx-auto mb-6 shadow-xl">
            <span className="text-5xl">🤍</span>
          </div>
          <Text type="Heading" variant="h1" className="text-eggs-dark mb-3">
            eggs Connect
          </Text>
          <Text type="Body" variant="lg" className="text-gray-600 mb-2">
            Digital Customer Experience Days
          </Text>
          <Text type="Body" variant="Base" className="text-gray-500">
            Vernetze dich mit Experten und Speakern
          </Text>
        </div>

        {/* Login Card */}
        <div className="bg-white rounded-2xl shadow-xl p-8 mb-6">
          <Text type="Heading" variant="h2" as="h2" className="text-center mb-6 text-gray-800">
            Anmelden
          </Text>

          {error && (
            <Alert type="error" className="mb-3">
              {error}
            </Alert>
          )}

          {/* Social Login Buttons */}
          <div className="space-y-3 mb-6">
            <MicrosoftButton
              disabled={isLoading}
              onClick={async () => {
                setError('');
                setIsLoading(true);
                try {
                  await msalAuthService.login();
                  navigate('/');
                } catch (err) {
                  if (err.errorCode === 'user_cancelled') return;
                  setError(err.message || 'Microsoft-Login fehlgeschlagen');
                } finally {
                  setIsLoading(false);
                }
              }}
            >
              Mit Microsoft anmelden
            </MicrosoftButton>

            <LinkedInButton
              disabled={isLoading}
              onClick={() => initiateOAuthLogin('LinkedIn')}
            >
              Mit LinkedIn anmelden
            </LinkedInButton>

          </div>

          {/* Divider */}
          <div className="relative my-6">
            <div className="absolute inset-0 flex items-center">
              <div className="w-full border-t border-gray-200"></div>
            </div>
            <div className="relative flex justify-center">
              <span className="px-4 bg-white text-sm text-gray-500">
                oder
              </span>
            </div>
          </div>

          {/* Email Login Link */}
          <button
            onClick={() => navigate('/login')}
            className="w-full h-11 px-6 text-[15px] font-medium text-eggs-dark hover:text-eggs-dark border-2 border-eggs-yellow hover:border-eggs-yellow-hover bg-eggs-yellow hover:bg-eggs-yellow-hover rounded-md transition-all"
          >
            Mit E-Mail anmelden
          </button>
        </div>

        {/* Register Link */}
        <div className="text-center">
          <Text type="Body" variant="sm" className="text-gray-600">
            Noch kein Konto?{' '}
            <button
              onClick={() => navigate('/register')}
              className="text-eggs-dark font-semibold hover:text-eggs-yellow-hover underline"
            >
              Jetzt registrieren
            </button>
          </Text>
        </div>
      </div>
    </div>
  );
}
